import { Body, Camera } from './types.js';
import { Renderer } from './rendering/renderer.js';
import { UIManager } from './ui/hud.js';
import { InputManager, PlaceState } from './ui/input.js';
import { makeArcadeBody } from './physics/bodies.js';
import { integrateBodies, advanceTrails } from './physics/integrator.js';

const canvas = document.getElementById('gameCanvas') as HTMLCanvasElement;
canvas.style.width = window.innerWidth + 'px';
canvas.style.height = window.innerHeight + 'px';

const camera: Camera = { centerX: 0, centerY: 0, zoom: 1.0 };
window._cam = camera;

const renderer = new Renderer(canvas);
const uiManager = new UIManager();
const inputManager = new InputManager();

let bodies: Body[] = uiManager.loadScenario();
let grabbedBody: Body | null = null;

// ─── Input ───

canvas.addEventListener('mousedown', (e) => {
  if (uiManager.getShowHelp()) { uiManager.toggleHelp(); return; }
  inputManager.onMouseDown(e, camera, bodies);
});

canvas.addEventListener('mousemove', (e) => inputManager.onMouseMove(e, camera));

canvas.addEventListener('mouseup', (e) => {
  inputManager.onMouseUp(e);
  if (e.button !== 0) return;

  const state = inputManager.placeState;
  const wasDragging = inputManager.isDragging;

  if (state.mode === 'grabbing' && state.body) {
    // Slingshot: velocity from drag distance in screen pixels
    const velScale = 0.1;
    state.body.vx = (state.screenCurrent.x - state.screenStart.x) * velScale;
    state.body.vy = (state.screenCurrent.y - state.screenStart.y) * velScale;
    grabbedBody = state.body;
  } else if (state.mode === 'placing') {
    const velScale = 0.1;
    const vx = (state.screenCurrent.x - state.screenStart.x) * velScale;
    const vy = (state.screenCurrent.y - state.screenStart.y) * velScale;
    bodies.push(makeArcadeBody(
      state.worldStart.x, state.worldStart.y,
      vx, vy,
      10 + Math.random() * 20,
      5 + Math.random() * 5
    ));
  }

  inputManager.clear();
  grabbedBody = null;
});

canvas.addEventListener('wheel', (e) => inputManager.onWheel(e, camera));
canvas.addEventListener('contextmenu', (e) => e.preventDefault());

window.addEventListener('keydown', (e) => {
  switch (e.key) {
    case ' ': e.preventDefault(); uiManager.setPaused(!uiManager.isPaused); break;
    case 'r': case 'R': bodies = uiManager.loadScenario(); grabbedBody = null; break;
    case 'h': case 'H': uiManager.toggleHelp(); break;
    case '+': case '=': uiManager.setTimeScale(uiManager.getTimeScale() * 1.25); break;
    case '-': case '_': uiManager.setTimeScale(uiManager.getTimeScale() / 1.25); break;
    case '0': uiManager.setTimeScale(1); break;
    case 's': case 'S': uiManager.cycleScenario(); bodies = uiManager.loadScenario(); grabbedBody = null; break;
    case 'Delete': case 'Backspace': if (grabbedBody) { bodies = bodies.filter(b => b !== grabbedBody); grabbedBody = null; } break;
  }
});

window.addEventListener('resize', () => renderer.resize());
renderer.resize();

// ─── Game loop ───

let lastTime = 0;
let trailTimer = 0;

function gameLoop(timestamp: number) {
  const rawDt = Math.min((timestamp - lastTime) / 1000, 0.05);
  lastTime = timestamp;

  if (!uiManager.isPaused) {
    const scaledDt = rawDt * uiManager.getTimeScale();
    const substeps = 4;
    const subDt = scaledDt / substeps;
    for (let s = 0; s < substeps; s++) integrateBodies(bodies, subDt);

    trailTimer += scaledDt;
    if (trailTimer > 0.05) { advanceTrails(bodies, 500); trailTimer = 0; }
  }

  renderer.clear();
  renderer.drawBackground(camera);
  renderer.drawTrails(bodies, camera);

  const drawOrder = grabbedBody ? bodies.filter(b => b !== grabbedBody) : bodies;
  renderer.drawBodies(drawOrder, camera);
  if (grabbedBody) renderer.drawBody(grabbedBody, camera);

  const state = inputManager.placeState;

  // Velocity arrow when placing (only at start of drag)
  if (state.mode === 'placing' && !inputManager.isDragging) {
    const ctx = renderer.ctx;
    const start = {
      x: (state.worldStart.x - camera.centerX) * camera.zoom + window.innerWidth / 2,
      y: (state.worldStart.y - camera.centerY) * camera.zoom + window.innerHeight / 2,
    };
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6,4]);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(state.screenCurrent.x, state.screenCurrent.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath();
    ctx.arc(start.x, start.y, 15 * camera.zoom, 0, Math.PI*2);
    ctx.fill();
  }

  // Highlight grabbed body + velocity preview
  if (state.mode === 'grabbing' && state.body) {
    const ctx = renderer.ctx;
    const body = state.body;
    const screenX = (body.x - camera.centerX) * camera.zoom + window.innerWidth / 2;
    const screenY = (body.y - camera.centerY) * camera.zoom + window.innerHeight / 2;
    const r = Math.max(2, body.radius * camera.zoom);

    ctx.strokeStyle = 'rgba(255,255,100,0.9)';
    ctx.lineWidth = 2.5;
    ctx.setLineDash([8,4]);
    ctx.beginPath();
    ctx.arc(screenX, screenY, r + 8*camera.zoom, 0, Math.PI*2);
    ctx.stroke();
    ctx.setLineDash([]);

    if (inputManager.isGrabbing) {
      const dx = state.screenCurrent.x - state.screenStart.x;
      const dy = state.screenCurrent.y - state.screenStart.y;
      if (Math.sqrt(dx*dx+dy*dy) > 2) {
        ctx.strokeStyle = 'rgba(100,255,100,0.8)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(screenX, screenY);
        ctx.lineTo(screenX + dx*0.5, screenY + dy*0.5);
        ctx.stroke();
        const angle = Math.atan2(dy, dx);
        const headLen = 12;
        ctx.beginPath();
        ctx.moveTo(screenX + dx*0.5, screenY + dy*0.5);
        ctx.lineTo(screenX + dx*0.5 - headLen*Math.cos(angle-0.4), screenY + dy*0.5 - headLen*Math.sin(angle-0.4));
        ctx.moveTo(screenX + dx*0.5, screenY + dy*0.5);
        ctx.lineTo(screenX + dx*0.5 - headLen*Math.cos(angle+0.4), screenY + dy*0.5 - headLen*Math.sin(angle+0.4));
        ctx.stroke();
      }
    }
  }

  uiManager.draw(renderer.ctx, window.innerWidth, window.innerHeight, bodies.length);
  requestAnimationFrame(gameLoop);
}

requestAnimationFrame(gameLoop);
console.log('%c⟐ Orbital Sim loaded. Press H for controls.', 'color: #88aaff; font-size: 14px');
